import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Dropdown, Input } from "antd";
import { Button } from "@/shared/components/button";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import type { PatientProfile } from "@/portals/patient/api/patientProfileApi";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { NotificationsMenu } from "@/shared/layout/NotificationsMenu";
import { HEADER_SEARCH_CLASS } from "@/shared/layout/PortalHeader";
import { SidebarToggle } from "@/shared/layout/PortalSidebar";
import { ConnectDataButton } from "@/features/patient/epic-connect/components/ConnectDataButton";

interface PatientHeaderProps {
  profile: PatientProfile;
  titleBlack?: string;
  titleRed?: string;
  description?: string;
}

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function PatientHeader({ profile, titleBlack, titleRed, description }: PatientHeaderProps) {
  const navigate = useNavigate();
  const signOut = usePatientAuthStore((s) => s.signOut);
  const [searchValue, setSearchValue] = useState("");
  const { avatarSrc } = usePatientProfile();

  const handleViewProfile = () => {
    navigate("/patient/profile");
  };

  const handleLogout = async () => {
    await signOut();
  };

  const menuContent = (
    <div className="w-[150px] overflow-hidden rounded-lg border border-[#DADDE5] bg-white shadow-[0_4px_12px_rgba(17,24,39,0.14)]">
      <Button
        variant="ghost"
        onClick={handleViewProfile}
        className="!block !h-auto !w-full !justify-start !rounded-none !px-4 !py-3 !text-left !text-sm !font-medium !text-[#180047] hover:!bg-[#F7F7F7] hover:!text-[#180047]"
      >
        View Profile
      </Button>
      <div className="h-px bg-[#E5E7EB]" />
      <Button
        variant="ghost"
        onClick={handleLogout}
        className="!block !h-auto !w-full !justify-start !rounded-none !px-4 !py-3 !text-left !text-sm !font-medium !text-[#E4002B] hover:!bg-[#FFF2F4] hover:!text-[#E4002B]"
      >
        Logout
      </Button>
    </div>
  );

  return (
    <header className="flex h-[73px] w-full shrink-0 items-center justify-between gap-3 border-b border-[#E5E7EB] bg-[#F7F7F7] px-4 lg:px-6">

      {/* Left — menu (below lg) + page title + description OR welcome message */}
      <div className="flex min-w-0 items-center gap-2">
        <SidebarToggle />
        <div className="flex min-w-0 flex-col justify-center gap-0">
          <h2 className="truncate text-base tracking-tight text-[#111827] sm:text-lg" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
            {titleBlack ?? `Welcome back, ${profile.firstName}!`}
          </h2>
          {titleBlack ? (
            description && <p className="hidden truncate text-[11px] leading-tight text-[#9CA3AF] sm:block" title={description}>{description}</p>
          ) : (
            <p className="hidden truncate text-xs leading-tight text-[#9CA3AF] sm:block">
              Here's what's happening with your health this month
            </p>
          )}
        </div>
      </div>

      {/* Right — Search + Connect + Bell + Avatar */}
      <div className="flex shrink-0 items-center gap-2 lg:gap-3">
        <Input
          prefix={<Icon icon="lucide:search" className="h-4 w-4 text-[#4B5563]" />}
          placeholder="Search anything..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className={HEADER_SEARCH_CLASS}
        />
        <ConnectDataButton />
        <NotificationsMenu />
        <Dropdown trigger={["click"]} popupRender={() => menuContent} placement="bottomRight">
          <Button
            variant="ghost"
            aria-label="Open profile menu"
            className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !bg-[#180047] !p-0 !text-center !text-xs !font-bold !leading-none !text-white hover:!bg-[#180047] hover:!opacity-85"
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              <span className="block leading-none !text-white">
                {getInitials(profile.firstName, profile.lastName)}
              </span>
            )}
          </Button>
        </Dropdown>
      </div>
    </header>
  );
}
